import { useCallback, useEffect, useRef, useState } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { ScopePopupPlacement } from './scope-popup-sizing-utils';
import { scopePopupSizing } from './scope-popup-sizing-utils';

/**
 * Keeps a scope popup within its live panel boundary for the full time it is open. The initial
 * activation measurement prevents a wide first paint; the observers cover responsive layout and
 * user-driven SideNav resizing after the popup has opened.
 */
export const useScopePopupSizing = (
	isOpen: boolean,
): {
	maxWidth: number;
	placement: ScopePopupPlacement;
	setTrigger: (trigger: HTMLElement | null) => void;
	updateSizing: (trigger?: HTMLElement | null) => void;
} => {
	const triggerRef = useRef<HTMLElement | null>(null);
	const [maxWidth, setMaxWidth] = useState(scopePopupSizing.defaultMaxWidth);
	const [placement, setPlacement] = useState<ScopePopupPlacement>('bottom-start');

	const setTrigger = useCallback((trigger: HTMLElement | null) => {
		triggerRef.current = trigger;
	}, []);

	const updateSizing = useCallback((trigger = triggerRef.current) => {
		if (trigger) {
			const sizing = scopePopupSizing.getSizing(trigger, fg('platform-dst-top-layer'));
			setMaxWidth(sizing.maxWidth);
			setPlacement(sizing.placement);
		}
	}, []);

	useEffect(() => {
		const trigger = triggerRef.current;
		if (!isOpen || !trigger) {
			return;
		}

		const update = () => updateSizing(trigger);
		const panelBoundary = scopePopupSizing.getPanelBoundary(trigger);
		const resizeObserver =
			typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(update);

		update();
		resizeObserver?.observe(trigger);
		if (panelBoundary) {
			resizeObserver?.observe(panelBoundary);
		}
		return () => {
			resizeObserver?.disconnect();
		};
	}, [isOpen, updateSizing]);

	return { maxWidth, placement, setTrigger, updateSizing };
};
